import React, { useMemo, useRef, useState } from "react";
import { Box, Typography, Popover } from "@mui/material";
import {
  ChevronRight as ChevronRightIcon,
  CalendarMonth as CalendarIcon,
} from "@mui/icons-material";
import { COLORS } from "../../styles/uiConstants";
import { useClinicTheme } from "../../theme";
import CalendarSurface, { DAY_INDEX_TO_TR, formatDateTR, parseYMD } from "./CalendarSurface";

/**
 * Kart tetikleyicili tarih seçici — seçili tarihi ve (varsa) o günün çalışma
 * saatlerini geniş bir satırda gösterir, tıklanınca takvim **içeriğin üstüne
 * binen** bir popover'da açılır; altındaki alanları aşağı itmez.
 *
 * Takvim yüzeyi `CalendarSurface` ile paylaşılır — uygulamadaki tüm tarih
 * seçicileri aynı takvimi kullanır.
 *
 * @param {string}   value             Seçili tarih "YYYY-MM-DD"
 * @param {Function} onChange          (dateStr) => void
 * @param {string}   [minDate]         Minimum seçilebilir tarih "YYYY-MM-DD"
 * @param {string}   [maxDate]         Maksimum seçilebilir tarih "YYYY-MM-DD"
 * @param {Array}    [workingSchedule] Klinik çalışma takvimi [{day, startTime, endTime}]
 * @param {string}   [label]           Üst etiket
 * @param {boolean}  [disabled]
 */
export default function TrCalendarPicker({
  value,
  onChange,
  minDate,
  maxDate,
  workingSchedule = [],
  label,
  disabled = false,
}) {
  const { palette } = useClinicTheme();
  const primary = palette.primary;
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const hasSchedule = workingSchedule.length > 0;

  // Seçilen günün çalışma bilgisi — tetikleyicide açık/kapalı olarak gösterilir.
  const selectedDayInfo = useMemo(() => {
    const d = parseYMD(value);
    if (!d || !hasSchedule) return null;
    const dayName = DAY_INDEX_TO_TR[d.getDay()];
    const entry = workingSchedule.find((s) => s.day === dayName);
    return entry
      ? { dayName, isClosed: false, startTime: entry.startTime, endTime: entry.endTime }
      : { dayName, isClosed: true };
  }, [value, workingSchedule, hasSchedule]);

  const handleSelect = (dateStr) => {
    onChange?.(dateStr);
    setOpen(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        ref={anchorRef}
        onClick={() => !disabled && setOpen((o) => !o)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          border: "1px solid",
          borderColor: open ? primary : COLORS.slate200,
          borderRadius: 2,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          bgcolor: COLORS.white,
          transition: "all 0.15s ease",
          "&:hover": disabled ? {} : { borderColor: primary, bgcolor: COLORS.slate50 },
        }}
      >
        <CalendarIcon sx={{ color: open ? primary : COLORS.slate400, fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {label && (
            <Typography sx={{ fontSize: 11, color: COLORS.slate400, fontWeight: 600, lineHeight: 1 }}>
              {label}
            </Typography>
          )}
          <Typography sx={{
            fontSize: 14,
            fontWeight: value ? 700 : 400,
            color: value ? COLORS.slate800 : COLORS.slate400,
            lineHeight: 1.4,
          }}>
            {value ? formatDateTR(value) : "Tarih seçin"}
          </Typography>
          {value && selectedDayInfo && (
            <Typography sx={{
              fontSize: 11,
              fontWeight: 600,
              color: selectedDayInfo.isClosed ? COLORS.error : COLORS.successDark,
              lineHeight: 1,
              mt: 0.3,
            }}>
              {selectedDayInfo.isClosed
                ? "Kapalı"
                : `${selectedDayInfo.startTime} – ${selectedDayInfo.endTime}`}
            </Typography>
          )}
        </Box>
        <ChevronRightIcon sx={{
          color: COLORS.slate400,
          fontSize: 18,
          transform: open ? "rotate(90deg)" : "rotate(0deg)",
          transition: "transform 0.2s ease",
        }} />
      </Box>

      {/* Takvim içeriğin ÜSTÜNE açılır — aşağıdaki alanlar yerinde kalır. */}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              width: anchorRef.current?.offsetWidth,
              minWidth: 280,
              maxWidth: 380,
              border: `1px solid ${COLORS.slate200}`,
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 12px 32px rgba(15, 23, 42, 0.14)",
            },
          },
        }}
      >
        <CalendarSurface
          value={value}
          onChange={handleSelect}
          minDate={minDate}
          maxDate={maxDate}
          workingSchedule={workingSchedule}
        />
      </Popover>
    </Box>
  );
}
